/**
 * Rich note / HTML fragment helpers for any user-generated notes or text blocks:
 * detect HTML vs plain, detect embedded <a> tags, convert HTML to plain text while
 * preserving line breaks (for client-side linkify via linkifyPlainTextParts).
 */

export type RichNotePlan =
  | { mode: 'html'; html: string }
  | { mode: 'plain'; text: string };

/** True when the string looks like an HTML fragment (tag open + close later). */
export function looksLikeHtmlFragment(s: string): boolean {
  return /<[a-z][\s\S]*>/i.test(s);
}

/** True when the fragment contains an HTML anchor (trust server links; skip linkify). */
export function htmlFragmentHasAnchorTag(html: string): boolean {
  return /<a[\s/>]/i.test(html);
}

/**
 * Remove <style>, <script>, and <noscript> blocks. Pasted notes (e.g. Apple Notes HTML)
 * often include <style>; DOMParser body textContent otherwise includes that CSS as visible text.
 */
export function stripNonVisibleHtmlBlocks(html: string): string {
  return html
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<noscript\b[^>]*>[\s\S]*?<\/noscript>/gi, '');
}

function decodeBasicHtmlEntities(s: string): string {
  return s
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&quot;/gi, '"');
}

/**
 * Insert newlines before DOMParser textContent so adjacent blocks (e.g. </p><p>)
 * do not glue separate lines or URLs into one run.
 */
function injectBlockSeparatorsBeforeParse(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '</p>\n')
    .replace(/<\/div>/gi, '</div>\n')
    .replace(/<\/li>/gi, '</li>\n')
    .replace(/<\/tr>/gi, '</tr>\n')
    .replace(/<\/h([1-6])>/gi, '</h$1>\n');
}

function collapseExcessiveBlankLines(s: string): string {
  return s.replace(/\n{3,}/g, '\n\n').trim();
}

/** Strip tags without DOMParser (SSR / tests). */
function htmlFragmentToPlainTextFallback(html: string): string {
  let s = injectBlockSeparatorsBeforeParse(html);
  s = s.replace(/<[^>]+>/g, '');
  return collapseExcessiveBlankLines(decodeBasicHtmlEntities(s).replace(/\u00a0/g, ' '));
}

/**
 * Convert an HTML fragment to plain text for linkifyPlainTextParts.
 * Preserves line breaks between block elements. Prefer DOMParser when available.
 */
export function htmlFragmentToPlainText(html: string): string {
  const trimmed = stripNonVisibleHtmlBlocks(html).trim();
  if (!trimmed) return '';
  const normalized = injectBlockSeparatorsBeforeParse(trimmed);
  try {
    if (typeof DOMParser !== 'undefined') {
      const doc = new DOMParser().parseFromString(normalized, 'text/html');
      const text = doc.body?.textContent ?? '';
      return collapseExcessiveBlankLines(text.replace(/\u00a0/g, ' '));
    }
  } catch {
    // use fallback
  }
  return htmlFragmentToPlainTextFallback(trimmed);
}

/**
 * Decide how to render a note: raw HTML (when anchors exist) vs plain string for linkify.
 */
export function planRichNoteParts(raw: string): RichNotePlan {
  const s = stripNonVisibleHtmlBlocks(raw ?? '');
  if (!s.trim()) {
    return { mode: 'plain', text: '' };
  }
  const htmlish = looksLikeHtmlFragment(s);
  const hasAnchor = htmlFragmentHasAnchorTag(s);
  if (htmlish && hasAnchor) {
    return { mode: 'html', html: s };
  }
  const text = htmlish ? htmlFragmentToPlainText(s) : s;
  return { mode: 'plain', text };
}
